<template>
    <div class="v-select-container">
        <select :id="id" :disabled="disabled" :required="required" class="form-control" :name="name" ref="selector">
            <option v-if="useNativePlaceholder"></option>
            <option v-if="placeholder && !useNativePlaceholder" value="0" >{{ placeholder }}</option>
            <template v-if="options && (!groupedOptions || groupedOptions.length == 0)">
                <option v-for="option in options" :key="option[valueKey]" :value="option[valueKey]" :selected="value == option[valueKey]">{{ option[label] }}</option>
            </template>
            <template v-if="groupedOptionsFiltered && groupedOptionsFiltered.length == 1" >
                <option v-for="option in groupedOptionsFiltered[0].options" :key="option[valueKey]" :value="option[valueKey]" :selected="value == option[valueKey]">{{ option[label] }}</option>
            </template>
            <template v-if="groupedOptionsFiltered && groupedOptionsFiltered.length > 1">
                <optgroup v-for="(group, group_index) in groupedOptionsFiltered" :key="group_index" :label="group.label">
                    <option v-for="option in group.options" :key="option[valueKey]" :value="option[valueKey]" :selected="value == option[valueKey]">{{ option[label] }}</option>
                </optgroup>
            </template>
        </select>
    </div>
</template>

<script>
import $ from 'jquery';
import 'select2';

export default {
    compatConfig: {
        WATCH_ARRAY: false,
    },
    
    name: 'VSelect',

    props: {
        id: {
            required: false,
        },

        name: {
            required: true,
        },

        valueKey: {
            required: false,
            default: 'id',
        },

        label: {
            required: false,
            default: 'label',
        },

        placeholder: {
            required: false,
            default: null,
        },

        // Compared to the 'placeholder' prop located above,
        // this prop creates a native selects2 placeholder.
        // i.e, a placeholder without a value.
        useNativePlaceholder: {
            required: false,
            default: false
        },

        itemsPerPage: {
            required: false,
            default: 10,
        },

        loadFromUrl: {
            required: false,
            default: null,
        },

        urlParams: {
            required: false,
            default: null
        },

        options: {
            required: false,
            default: null,
        },

        groupedOptions: {
            required: false,
            default: null,
        },

        value: {
            required: false,
            default: null,
        },

        templateCallback: {
            required: false,
            type: Function,
            default: null,
        },

        dropdownParent: {
            required: false,
            type: String,
            default: null,
        },

        highlight: {
            type: Boolean,
            default: false,
        },

        disabled: {
            type: Boolean,
            default: false,
        },

        required: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            query: {},
        }
    },

    mounted(){
        this.initSelect2();
    },

    methods: {
        initSelect2() {
            var vm = this;

            this.$nextTick( () => {

                let options = {
                    theme: 'bootstrap',
                    minimumResultsForSearch: 10,
                    language: {
                        errorLoading: function() {
                            return trans('clients.select_1')
                        },
                        loadingMore: function() {
                            return trans('clients.select_2')
                        },
                        noResults: function() {
                            return trans('clients.select_3')
                        },
                        searching: function(params) {
                            this.query = params;

                            return trans('clients.select_4')
                        }
                    },
                };

                if (this.templateCallback && typeof this.templateCallback == 'function') {
                    options.templateResult = this.templateCallback;
                } else if (this.highlight) {
                    options.templateResult = this.templateHighlightCallback;
                }

                if (this.placeholder) {
                    options.placeholder = this.placeholder;
                }

                if (this.dropdownParent) {
                    options.dropdownParent = $(this.dropdownParent);
                }

                if (this.loadFromUrl) {
                    options.ajax = {
                        url: this.loadFromUrl,
                        delay: 250,
                        dataType: 'json',
                        data: function (params) {
                            var query = {
                                search: params.term,
                                page: params.page || 1,
                                ...vm.urlParams
                            }
                            return query;
                        },
                        processResults: function (data) {
                            return {
                                results: data.data.map(item => {
                                    return {
                                        id: item[vm.valueKey],
                                        text: item[vm.label],
                                        subtext: item['subtext'],
                                    }
                                }),
                                pagination: data.pagination,
                            };
                        }
                    }
                    options.minimumResultsForSearch = this.itemsPerPage;
                }

                $(this.$refs.selector).select2(options).on('select2:select', function (e) {
                    vm.$emit('input', e.params.data.id);
                });
            });
        },

        destroySelect2() {
            if ($(this.$refs.selector).hasClass('select2-hidden-accessible')) {
                $(this.$refs.selector).select2('destroy').off('select2:select');
            }
        },

        templateHighlightCallback(state) {
            if (!state.id) {
                return state.text;
            }

            let term = query.term || '';
            let $result = this.highlightMatch(state, term);

            if (state.subtext !== undefined 
                && state.subtext !== '' 
                && state.subtext !== null
            ) {
                $result.append(
                    '<div class="select2-rendered_subtext">' + state.subtext + '</div>'
                );
            }

            return $result;
        },

        highlightMatch(state, term) {
            let match = state.text.toUpperCase().indexOf(term.toUpperCase());
            let $result = $('<div></div>');
            
            if (match < 0) {
                return $result.text(state.text);
            }
            
            $result.text(state.text.substring(0, match));
            
            let $match = $('<span class="select2-rendered__match"></span>');
            $match.text(state.text.substring(match, match + term.length));
            
            $result.append($match);
            $result.append(state.text.substring(match + term.length));

            return $result;
        }
    },

    watch: {
        options: {
            handler: function () {
                this.destroySelect2();
                this.initSelect2();
            },

            deep: true
        },

        groupedOptions: {
            handler: function () {
                this.destroySelect2();
                this.initSelect2();
            },

            deep: true
        }
    },

    computed: {
        groupedOptionsFiltered() {
            return this.groupedOptions ? this.groupedOptions.filter(group => {
                return group.options && group.options.length > 0;
            }) : [];
        }
    }
}
</script>

<style>
.v-select-container .select2-container {
    width: 100% !important;
}

.select2-results__option:empty {
    display: none;
}

.select2-rendered__match {
  text-decoration: underline;
  color: black;
  background-color: yellow;
}

.select2-rendered_subtext {
    font-size: smaller;
}
</style>
